import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

function resolveDefaultBaseURL(): string {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
    const apiUrl = env?.VITE_API_URL?.replace(/\/$/, '');
    if (apiUrl) return `${apiUrl}/api/v1`;
    if (env?.DEV) return '/api/v1';
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/v1`;
  }
  return 'http://localhost:3000/api/v1';
}

export class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;
  private accessToken: string | null = null;

  constructor(baseURL: string = resolveDefaultBaseURL()) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add access token
    this.client.interceptors.request.use(
      (config) => {
        // Try to get token from localStorage if not set
        let token = this.accessToken;
        if (!token && typeof window !== 'undefined') {
          token = localStorage.getItem('accessToken');
        }
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.accessToken = null;
          if (typeof window !== 'undefined') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /* ---------------- SUPPORT TICKETS ---------------- */

  async getSupportTickets(portalType: string, params?: any): Promise<any> {
    return this.get(`/${portalType}/support-tickets`, { params });
  }

  async getSupportTicketById(portalType: string, ticketId: string): Promise<any> {
    return this.get(`/${portalType}/support-tickets/${ticketId}`);
  }

  async createSupportTicket(data: any): Promise<any> {
    const portalType = data.portalType || 'customer';
    return this.post(`/${portalType}/support-tickets`, data);
  }

  async addSupportTicketComment(portalType: string, ticketId: string, comment: string): Promise<any> {
    return this.post(`/${portalType}/support-tickets/${ticketId}/comment`, { comment });
  }

  async resolveSupportTicket(portalType: string, ticketId: string, note?: string): Promise<any> {
    return this.post(`/${portalType}/support-tickets/${ticketId}/resolve`, { note });
  }

  async escalateSupportTicket(portalType: string, ticketId: string, note?: string): Promise<any> {
    return this.post(`/${portalType}/support-tickets/${ticketId}/escalate`, { note });
  }

  /* ---------------- RISK & INCIDENT ---------------- */

  async getRisks(portalType: string, params?: any): Promise<any> {
    return this.get(`/${portalType}/risks`, { params });
  }

  async getIncidents(portalType: string, params?: any): Promise<any> {
    return this.get(`/${portalType}/incidents`, { params });
  }
}

export const apiClient = new ApiClient();

